import React, { useMemo, useState } from 'react';
import { TiktokAudiosTableRowV2 } from '../../../../TikTok.types';
import TiktokAudioModal from '../../details/TiktokAudioModal/TiktokAudioModal';
import styles from './TiktokCollectionsAudios.module.css';
import Skeleton from '../../../../../../ui/Skeleton/Skeleton';
import { numberWithCommas } from '../../../../../../helpers';
import { ClientMonitoredTiktokAudioRelationship, GetTiktokAudioCollectionAudioStatsParams } from '@round/api';
import TiktokCollectionsAudiosTable from './components/TiktokCollectionsAudiosTable/TiktokCollectionsAudiosTable';

type TiktokCollectionsAudiosProps = {
    rows: TiktokAudiosTableRowV2[];
    loading: boolean;
    errorLoading: boolean;
    count: number;
    page: number;
    pageSize: number;
    search: string;
    ordering: GetTiktokAudioCollectionAudioStatsParams['ordering'];
    audioIdForModal: number | undefined;
    setPage: (page: number) => void;
    setPageSize: (pageSize: number | undefined) => void;
    setSearch: (query: string) => void;
    setOrdering: (order: GetTiktokAudioCollectionAudioStatsParams['ordering']) => void;
    setAudioIdForModal: (id: number | undefined) => void;
    setAlternateName: (rel: ClientMonitoredTiktokAudioRelationship) => void;
};

const TiktokCollectionsAudios = ({
    rows,
    setAudioIdForModal,
    audioIdForModal,
    errorLoading,
    page,
    pageSize,
    setPageSize,
    search,
    setSearch,
    ordering,
    setOrdering,
    loading,
    setPage,
    count,
    setAlternateName,
}: TiktokCollectionsAudiosProps) => {
    const [audiosRowsDisplayed, setAudiosRowsDisplayed] = useState(0);
    const noDataLabel = errorLoading ? 'Sorry, we are experiencing technical issues' : 'No audios found';
    const audiosRecordsShowingLabel = `displaying ${audiosRowsDisplayed} out of ${numberWithCommas(count)} song${
        count === 1 ? '' : 's'
    }`;

    const selectedRow = useMemo(() => rows.find((row) => row.music_id === audioIdForModal), [audioIdForModal, rows]);

    return (
        <>
            <div className={styles.titles}>
                <p className={styles.title}>Top Songs</p>
                <p className={styles.subtitle}>{loading ? <Skeleton width="20rem" /> : audiosRecordsShowingLabel}</p>
            </div>
            <TiktokCollectionsAudiosTable
                loading={loading}
                data={rows}
                count={count}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                ordering={ordering}
                setOrdering={setOrdering}
                search={search}
                setSearch={setSearch}
                noDataLabel={noDataLabel}
                onRowClick={(row) => setAudioIdForModal(row.music_id)}
                onPaginationChange={(state) => setAudiosRowsDisplayed(state.pageRows.length)}
            />
            <TiktokAudioModal
                isModalOpen={!!audioIdForModal}
                closeModal={() => setAudioIdForModal(undefined)}
                audioId={audioIdForModal}
                monitoredAudioStats={selectedRow}
                onAlternateNameUpdated={setAlternateName}
            />
        </>
    );
};

export default TiktokCollectionsAudios;
